import Link from "next/link";
import { FC, ReactElement } from "react";

import {
  lgTableCellStyle,
  mdTableCellStyle,
  smTableCellStyle,
  tableCellStyle,
  xlTableCellStyle,
} from "~/components/database/detail/database-content/PcDatabaseContent";
import { TableCellSize } from "~/components/database/detail/database-content/lib";
import OgpLink, {
  ExternalSiteData,
} from "~/components/database/detail/database-content/response-table/OgpLink";
import { Answer } from "~/components/database/detail/database-content/types";
import ExhaustiveError from "~/lib/errors";

type AnswerContentProps = {
  isOwner?: boolean;
  answer?: Answer;
  size?: TableCellSize;
  children: ReactElement;
};

export function buildAnswerContentBody(
  answer: Answer,
  externalSiteData: ExternalSiteData | undefined = undefined // 一部のコンポーネントのみで使用するため、デフォルト値は undefined とする
): ReactElement {
  switch (answer.__typename) {
    case "TextAnswer":
      return <p>{answer.textValue}</p>;
    case "NumberAnswer":
      return <p>{answer.numberValue}</p>;
    case "LocationAnswer":
      const { latitude, longitude } = answer.locationValue;
      return (
        <Link
          href={`https://www.google.com/maps?q=${latitude},${longitude}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <p>{`${latitude}, ${longitude}`}</p>
        </Link>
      );
    case "ReferenceAnswer":
      return (
        <Link href={`/item/${answer.referenceValue}`}>
          <p>#{answer.referenceValue}</p>
        </Link>
      );
    case "LongTextAnswer":
      const longTextValue = answer.longTextValue.split("\n").map((line, i) => (
        <span key={i}>
          {line}
          <br />
        </span>
      ));
      return <p>{longTextValue}</p>;
    case "ShortTextAnswer":
      return <p>{answer.shortTextValue}</p>;
    case "DateAnswer":
      return <p>{answer.dateValue}</p>;
    case "UrlAnswer":
      return <OgpLink url={answer.urlValue} data={externalSiteData} />;
    case "ImageUrlAnswer":
      return (
        <img
          src={answer.imageUrlValue}
          alt={answer.imageUrlValue}
          width={"100%"}
          height={"auto"}
        />
      );
    default:
      throw new ExhaustiveError(answer);
  }
}

export const AnswerContent: FC<AnswerContentProps> = ({ children, size }) => {
  const answerStyles = [tableCellStyle];
  switch (size) {
    case "sm":
      answerStyles.push(smTableCellStyle);
      break;
    case "md":
      answerStyles.push(mdTableCellStyle);
      break;
    case "lg":
      answerStyles.push(lgTableCellStyle);
      break;
    case "xl":
      answerStyles.push(xlTableCellStyle);
      break;
  }

  return (
    <div role="cell" css={answerStyles}>
      {children}
    </div>
  );
};
